import {useQuery, type QueryKey} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import type {ChangeGroupPayload} from '../../types/group-types'
import {useChangeGroupGeneration, useChangeGroupGenerationActions} from '../../stores/selectors'
import {isCompletedChangeGroupState} from '../../helpers/change-groups'
import {useCallback} from 'react'

interface QueryKeyFields {
  headSha: string | undefined
  baseSha: string | undefined
  changeGroupsURL: string
}

function changeGroupDataKey({baseSha, headSha, changeGroupsURL}: QueryKeyFields): QueryKey {
  return [PageData.changeGroups, changeGroupsURL, `baseSha:${baseSha}`, `headSha:${headSha}`]
}
export function useChangeGroupsData(headSha: string | undefined, baseSha: string | undefined) {
  const changeGroupsURL = usePageDataUrl(PageData.changeGroups)
  const queryKey = changeGroupDataKey({headSha, baseSha, changeGroupsURL})
  const {updateChangeGroupGeneration} = useChangeGroupGenerationActions()
  const changeGroupGeneration = useChangeGroupGeneration()

  const initialData = useCallback(() => {
    const orchestrationState = changeGroupGeneration.latestChangeGroupOrchestration?.state

    // Only set initial data to be cached if orchestration has completed
    if (orchestrationState && isCompletedChangeGroupState(orchestrationState)) {
      return changeGroupGeneration
    }

    return undefined
  }, [changeGroupGeneration])

  return useQuery<ChangeGroupPayload>({
    queryKey,
    queryFn: async () => {
      const response = await reactFetch(changeGroupsURL)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const json = await response.json()

      // Update Zustand store immediately when data is fetched
      updateChangeGroupGeneration(json)

      return json
    },
    initialData: initialData(),
    staleTime: Infinity,
  })
}

export function useChangeGroupsDataQueryKey(headSha: string | undefined, baseSha: string | undefined): QueryKey {
  const changeGroupsURL = usePageDataUrl(PageData.changeGroups)
  return changeGroupDataKey({headSha, baseSha, changeGroupsURL})
}
